/**
 * Native Android reminders for the Play Store app.
 *
 * WHY THIS EXISTS: the Android app is a WebView around memocal.app, and Android
 * WebViews do not implement the Web Push API — so the browser notifications that
 * work in Chrome/the PWA never arrive inside the installed app. Instead we hand
 * the reminders to Android itself: the system holds them and fires them at the
 * right minute even if MeMo is closed, the phone is locked, or there is no
 * connection at that moment.
 *
 * Everything here is a no-op on the web, where Web Push already does the job.
 */
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativeApp } from './platform';
import apiClient from '../api/client';
import { ALERT_SOUNDS, ALARM_KEYS } from './sounds';

// Lives in utils/platform.js now, so pages that only need to know "are we in
// the Play Store app?" can ask without dragging the notifications plugin in.
// Re-exported because this module's own callers already import it from here.
export { isNativeApp };

// A "message" reminder is a silent on-screen note (same as on the web), so its
// channel deliberately has no sound.
const CH_MESSAGE = 'memo-reminders';
// An "alarm" reminder plays the melody the user chose in Settings. Android
// freezes a channel's sound the moment the channel is created and never lets
// it change, so each melody gets its OWN channel and we simply post to the
// right one. Side benefit: the user can retune any of them from Android's own
// notification settings.
const SOUND_NAMES = ALERT_SOUNDS.map(s => s.value);
const chFor = (sound) => `memo-alarm-${sound}`;
let channelsReady = false;

async function ensureChannels() {
  if (channelsReady) return;
  try {
    await LocalNotifications.createChannel({
      id: CH_MESSAGE, name: 'MeMo — promemoria', importance: 4, visibility: 1, vibration: true,
    });
    for (const s of ALERT_SOUNDS) {
      await LocalNotifications.createChannel({
        id: chFor(s.value),
        name: `MeMo — sveglia: ${s.name}`,
        // The .wav files live in the Android project's res/raw and are rendered
        // from the very same tones as the web version by mobile/scripts/genSounds.js.
        sound: `${s.value}.wav`,
        importance: 5, visibility: 1, vibration: true,
      });
    }
  } catch (_) { /* iOS has no channels — scheduling still works */ }
  channelsReady = true;
}

// ── The two buttons on the alarm notification ────────────────────────────────
// Android shows them right on the lock screen, so a ringing alarm can be dealt
// with in ONE tap instead of unlocking, finding MeMo and opening it.
// Note on how far this can go: the notification component builds these buttons
// as "open the app" intents, so tapping one does bring MeMo to the front for a
// moment. Stopping the ringing with the app fully closed would mean replacing
// the whole notification machinery with hand-written Android code — see the
// note in useNativeAlarms.js.
const ACTION_TYPE = 'memo-alarm';
const SNOOZE_MIN = 5;
const ACTION_L = {
  it: { stop: 'Interrompi', snooze: `+${SNOOZE_MIN} min` },
  en: { stop: 'Stop', snooze: `+${SNOOZE_MIN} min` },
  es: { stop: 'Basta', snooze: `+${SNOOZE_MIN} min` },
  de: { stop: 'Stopp', snooze: `+${SNOOZE_MIN} Min` },
  fr: { stop: 'Stop', snooze: `+${SNOOZE_MIN} min` },
  nl: { stop: 'Stop', snooze: `+${SNOOZE_MIN} min` },
  pt: { stop: 'Parar', snooze: `+${SNOOZE_MIN} min` },
  ru: { stop: 'Стоп', snooze: `+${SNOOZE_MIN} мин` },
  pl: { stop: 'Stop', snooze: `+${SNOOZE_MIN} min` },
  ro: { stop: 'Stop', snooze: `+${SNOOZE_MIN} min` },
  zh: { stop: '停止', snooze: `+${SNOOZE_MIN} 分钟` },
  ja: { stop: '停止', snooze: `+${SNOOZE_MIN}分` },
  hi: { stop: 'बंद', snooze: `+${SNOOZE_MIN} मिनट` },
  ko: { stop: '중지', snooze: `+${SNOOZE_MIN}분` },
  ar: { stop: 'إيقاف', snooze: `+${SNOOZE_MIN} د` },
};
let actionsReady = false;

async function ensureActions(lang) {
  if (actionsReady) return;
  const a = ACTION_L[String(lang || 'it').split('-')[0]] || ACTION_L.en;
  try {
    await LocalNotifications.registerActionTypes({
      types: [{
        id: ACTION_TYPE,
        actions: [
          { id: 'stop', title: a.stop },
          { id: 'snooze', title: a.snooze },
        ],
      }],
    });
    actionsReady = true;
  } catch (_) { /* iOS/older Android: the notification simply has no buttons */ }
}

/**
 * A button on a ringing alarm was tapped.
 *   stop   — silence it and leave it at that
 *   snooze — silence it and ring again in a few minutes, same melody
 */
export async function handleAlarmAction(actionId, notification) {
  if (!isNativeApp()) return;
  await silenceStartedAlarms();
  if (actionId !== 'snooze') return;
  const x = notification?.extra || {};
  const at = Date.now() + SNOOZE_MIN * 60000;
  try {
    const rings = [];
    for (let k = 0; k < ALARM_REPEATS; k++) {
      rings.push({
        // Deliberately far from the original ids, so a snooze can never
        // overwrite (or be cancelled with) the alarm it came from.
        id: (Number(notification?.id || 0) + 7001 + k) % 2147483000,
        title: notification?.title || 'MeMo',
        body: notification?.body || '',
        schedule: { at: new Date(at + k * ALARM_GAP_MS), allowWhileIdle: true },
        channelId: x.ch || CH_MESSAGE,
        smallIcon: 'ic_stat_icon_config_sample',
        actionTypeId: ACTION_TYPE,
        group: `memo-snooze-${notification?.id}`,
        extra: { ...x, base: at },
      });
    }
    await LocalNotifications.schedule({ notifications: rings });
  } catch (_) { /* a failed snooze must not throw into the UI */ }
}

// Which melody this reminder should play: the one saved with the event wins
// (it's what the user picked when creating it), then this device's default.
function soundFor(ev) {
  const fromEvent = ev?.alertSound;
  if (fromEvent && SOUND_NAMES.includes(fromEvent)) return fromEvent;
  try {
    const local = localStorage.getItem(ALARM_KEYS.sound);
    if (local && SOUND_NAMES.includes(local)) return local;
  } catch (_) {}
  return 'campanella';
}

async function ensurePermission() {
  try {
    const now = await LocalNotifications.checkPermissions();
    if (now.display === 'granted') return true;
    if (now.display === 'denied') return false; // don't nag: they said no
    const asked = await LocalNotifications.requestPermissions();
    return asked.display === 'granted';
  } catch (_) { return false; }
}

// Android notification ids are 32-bit ints, and they must be STABLE: the same
// reminder has to keep the same id across syncs, or we'd schedule duplicates.
function idFor(eventId, minutesBefore) {
  const s = `${eventId}|${minutesBefore}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 2147483000;
}

// "fra 10 minuti" / "in 10 minutes" / "在 10 分钟后" — Intl gives us all 15
// languages for free, with no phrase to translate by hand.
function whenText(minutesBefore, lang) {
  try {
    const rtf = new Intl.RelativeTimeFormat(lang || 'it', { numeric: 'auto' });
    if (minutesBefore === 0) return null;
    return minutesBefore % 60 === 0 && minutesBefore >= 60
      ? rtf.format(minutesBefore / 60, 'hour')
      : rtf.format(minutesBefore, 'minute');
  } catch (_) { return null; }
}

function timeText(iso, lang) {
  try {
    return new Date(iso).toLocaleTimeString(lang || 'it', { hour: '2-digit', minute: '2-digit' });
  } catch (_) { return ''; }
}

const MAX_SCHEDULED = 120;  // Android's own cap is far higher; this is our budget
const HORIZON_DAYS  = 7;

// ── The insistent alarm ──────────────────────────────────────────────────────
// A single notification is easy to sleep through. An "alarm" reminder is
// therefore posted several times, ~30 s apart, so it keeps ringing like a real
// alarm clock instead of chiming once and giving up.
// It stops the moment MeMo is opened: syncNativeAlarms() cancels everything
// pending and never re-schedules an alarm whose time has already come — so
// tapping the notification (which opens the app) silences the rest.
const ALARM_REPEATS  = 8;          // 8 rings ≈ 3.5 minutes of insistence
const ALARM_GAP_MS   = 30 * 1000;
// Only alarms coming up soon get the full treatment: repeats are cheap but not
// free, and the queue is rebuilt every time the app is opened anyway.
const REPEAT_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Silence an alarm that is ringing NOW: cancel the rings still queued and clear
 * the ones already in the tray.
 *
 * This is deliberately separate from syncNativeAlarms() and carries no
 * throttling of its own: opening MeMo must ALWAYS stop the ringing. (It used to
 * be part of the sync, which skips itself if it ran in the last few minutes —
 * so an alarm set a couple of minutes earlier could not be silenced by opening
 * the app, which is the one thing an alarm must let you do.)
 */
export async function silenceStartedAlarms() {
  if (!isNativeApp()) return 0;
  try {
    const now = Date.now();
    const pending = await LocalNotifications.getPending();
    // A ring whose alarm already began is part of what is going off right now.
    const ringing = (pending?.notifications || []).filter((n) => {
      const base = n?.extra?.base;
      return typeof base === 'number' && base <= now;
    });
    if (ringing.length) {
      await LocalNotifications.cancel({ notifications: ringing.map(n => ({ id: n.id })) });
    }
    await LocalNotifications.removeAllDeliveredNotifications().catch(() => {});
    return ringing.length;
  } catch (_) { return 0; }
}

/**
 * Re-hand every upcoming reminder to Android. Safe to call as often as we like:
 * it wipes what it scheduled before and re-schedules from scratch, so events
 * edited or deleted elsewhere can never leave a ghost reminder behind.
 */
export async function syncNativeAlarms(lang) {
  if (!isNativeApp()) return { scheduled: 0, skipped: 'not-native' };
  if (!(await ensurePermission())) return { scheduled: 0, skipped: 'no-permission' };
  await ensureChannels();
  await ensureActions(lang);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
  let days = {};
  try {
    const res = await apiClient.get('/events/week', { params: { start: today, tz } });
    days = res.data || {};
  } catch (_) {
    // Offline or server hiccup: keep whatever Android already holds rather than
    // cancelling it — stale reminders beat no reminders at all.
    return { scheduled: 0, skipped: 'fetch-failed' };
  }

  const now = Date.now();
  const horizon = now + HORIZON_DAYS * 86400000;
  const notifications = [];
  Object.values(days).flat().forEach((ev) => {
    if (!ev?.start) return;
    const startMs = new Date(ev.start).getTime();
    if (!Number.isFinite(startMs)) return;
    const mins = Array.isArray(ev.alertMinutesList) && ev.alertMinutesList.length
      ? ev.alertMinutesList
      : (ev.alertMinutes != null ? [ev.alertMinutes] : []);
    mins.forEach((m) => {
      const at = startMs - m * 60000;
      // Already gone off (or too far ahead): nothing to schedule. Skipping the
      // past is also what silences a ringing alarm when the app is opened —
      // the repeats below are simply not put back.
      if (at <= now || at > horizon) return;
      const isAlarm = ev.alertType === 'alarm';
      const when = whenText(m, lang);
      const body = [timeText(ev.start, lang), when].filter(Boolean).join(' · ');
      const rings = isAlarm && at - now < REPEAT_WINDOW_MS ? ALARM_REPEATS : 1;
      const channelId = isAlarm ? chFor(soundFor(ev)) : CH_MESSAGE;
      for (let k = 0; k < rings; k++) {
        notifications.push({
          id: idFor(ev.id, `${m}r${k}`),
          title: ev.title || 'MeMo',
          body,
          schedule: { at: new Date(at + k * ALARM_GAP_MS), allowWhileIdle: true },
          channelId,
          // Only an alarm gets the Stop / +5 min buttons: a plain reminder is
          // a silent note, there's nothing to switch off.
          ...(isAlarm ? { actionTypeId: ACTION_TYPE } : {}),
          smallIcon: 'ic_stat_icon_config_sample',
          // Android bundles the rings of one alarm under a single entry in the
          // tray, instead of eight loose copies of the same reminder.
          group: `memo-${ev.id}-${m}`,
          // `base` = when this alarm STARTED ringing. It's what lets us tell,
          // later, which pending rings belong to an alarm that is going off
          // right now and must be silenced — see silenceStartedAlarms().
          // `ch` travels along so a snoozed alarm rings with the same melody.
          extra: { url: '/', base: at, ch: channelId },
        });
      }
    });
  });

  // Same event+lead-time can appear on two days of the week window (a recurring
  // series shares one id): keep one notification per id.
  const unique = [...new Map(notifications.map(n => [n.id, n])).values()]
    .sort((a, b) => a.schedule.at - b.schedule.at)
    .slice(0, MAX_SCHEDULED);

  try {
    const pending = await LocalNotifications.getPending();
    if (pending?.notifications?.length) await LocalNotifications.cancel(pending);
    // The user is looking at MeMo right now: an alarm still ringing has served
    // its purpose, and its rings shouldn't stay piled up in the tray.
    await LocalNotifications.removeAllDeliveredNotifications().catch(() => {});
    if (unique.length) await LocalNotifications.schedule({ notifications: unique });
  } catch (err) {
    return { scheduled: 0, skipped: 'schedule-failed', error: err?.message };
  }

  // Tell the server the phone is covered, so it doesn't add a Google/Outlook
  // reminder on top of the one Android will now fire. Once a day is plenty.
  try {
    const stampKey = 'memo_native_seen';
    const today = new Date().toLocaleDateString('en-CA');
    if (localStorage.getItem(stampKey) !== today) {
      await apiClient.post('/push/native-app');
      localStorage.setItem(stampKey, today);
    }
  } catch (_) { /* best effort — never worth failing the sync over */ }

  return { scheduled: unique.length };
}
